import { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { LanguageProvider } from './i18n/LanguageContext';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import analytics from './analytics';

// Lazy-load everything below the fold
const About          = lazy(() => import('./components/About'));
const Gallery        = lazy(() => import('./components/Gallery'));
const Amenities      = lazy(() => import('./components/Amenities'));
const Activities     = lazy(() => import('./components/Activities'));
const PricingContact = lazy(() => import('./components/PricingContact'));
const FAQ            = lazy(() => import('./components/FAQ'));
const Footer         = lazy(() => import('./components/Footer'));

const BookingWidget = () => {
  return (
    <div className="booking-widget">
      <div className="booking-widget-price">
        <span className="booking-widget-from">From</span>
        <span className="booking-widget-amount">CHF 150</span>
        <span className="booking-widget-night">/ night</span>
      </div>
      <div className="booking-widget-stars">★★★★★ <span>Saas-Fee</span></div>
      <button className="btn booking-widget-btn" onClick={() => { window.dispatchEvent(new Event('open-booking-widget')); analytics.ctaClick('widget_book_now'); }}>
        Book Now
      </button>
      <a href="#gallery" className="booking-widget-tour" onClick={(e) => { e.preventDefault(); document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' }); }}>
        🏠 View 3D Tour
      </a>
    </div>
  );
};

function AppInner() {
  useEffect(() => {
    const cur  = document.getElementById('cursor');
    const ring = document.getElementById('cursorRing');
    if (!cur || !ring) return;

    const onMove = (e) => {
      cur.style.left  = e.clientX + 'px';
      cur.style.top   = e.clientY + 'px';
      setTimeout(() => { ring.style.left = e.clientX + 'px'; ring.style.top = e.clientY + 'px'; }, 60);
    };
    document.addEventListener('mousemove', onMove);

    const grow   = () => { cur.style.width = '16px'; cur.style.height = '16px'; ring.style.width = '52px'; ring.style.height = '52px'; };
    const shrink = () => { cur.style.width = '10px'; cur.style.height = '10px'; ring.style.width = '36px'; ring.style.height = '36px'; };
    document.querySelectorAll('a, button').forEach(el => { el.addEventListener('mouseenter', grow); el.addEventListener('mouseleave', shrink); });

    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.style.opacity = 1; e.target.style.transform = 'translateY(0)'; } });
    }, { threshold: 0.15 });
    document.querySelectorAll('.amenity-pill, .activity-card, .stat, .fact-item').forEach(el => {
      el.style.transition = 'opacity .7s ease, transform .7s ease';
      el.style.opacity = 0;
      el.style.transform = 'translateY(28px)';
      io.observe(el);
    });

    const sections = document.querySelectorAll('section[id], div[id]');
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting && e.target.id) analytics.sectionView(e.target.id); });
    }, { threshold: 0.4 });
    sections.forEach(s => sectionObserver.observe(s));

    analytics.pageView(window.location.pathname);
    const cleanupScroll      = analytics.initScrollDepth();
    const cleanupEngagement  = analytics.initEngagementTime();

    return () => {
      document.removeEventListener('mousemove', onMove);
      sectionObserver.disconnect();
      cleanupScroll();
      cleanupEngagement();
    };
  }, []);

  return (
    <div className="App">
      <div className="cursor" id="cursor" />
      <div className="cursor-ring" id="cursorRing" />
      <header><Navbar /></header>
      <BookingWidget />
      <main>
        <Hero />
        <Suspense fallback={null}>
          <About />
          <Gallery />
          <Amenities />
          <Activities />
          <PricingContact />
          <FAQ />
        </Suspense>
      </main>
      <Suspense fallback={null}><Footer /></Suspense>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppInner />
    </LanguageProvider>
  );
}

export default App;
